import { Checkbox } from "../ui/checkbox";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { MessageCircle, Package, Shield, StickyNote, Users } from "lucide-react";

export const QuotationForm = ({ register, setValue, formValues, customers }) => (
  <>
    <Card className="border-2 border-gray-300">
      <div className="bg-black px-3 py-2">
        <h3 className="font-bold text-[#D4AF37] flex items-center gap-2 text-sm">
          <Package className="h-4 w-4" />
          Package Options
        </h3>
      </div>
      <CardContent className="p-3 space-y-2">
        {[
          { key: 'loading_crew', label: 'Loading Crew Only', badge: 'LABOUR ONLY', color: 'green' },
          { key: 'man_and_van', label: 'Man & Van', badge: 'BASIC', color: 'blue' },
          { key: 'economy', label: 'Economy Move', badge: 'BUDGET', color: 'gray' },
          { key: 'comfort', label: 'Comfort Move', badge: 'RECOMMENDED', color: 'gold' },
          { key: 'comfort_plus', label: 'Comfort Plus', badge: '★ POPULAR', color: 'black' },
          { key: 'premium', label: 'Premium', badge: 'PREMIUM', color: 'gold' },
        ].map(pkg => (
          <div key={pkg.key} className={`p-2 rounded border ${pkg.color === 'gold' ? 'border-[#D4AF37] bg-yellow-50' : pkg.color === 'black' ? 'border-black bg-gray-900' : pkg.color === 'blue' ? 'border-blue-400 bg-blue-50' : pkg.color === 'green' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formValues[`show_${pkg.key}`]}
                onCheckedChange={(c) => setValue(`show_${pkg.key}`, c)}
                className={pkg.color === 'black' ? 'border-[#D4AF37]' : ''}
              />
              <span className={`text-sm font-medium ${pkg.color === 'black' ? 'text-white' : ''}`}>{pkg.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${pkg.color === 'gold' ? 'bg-[#D4AF37] text-black' : pkg.color === 'black' ? 'bg-[#D4AF37] text-black' : pkg.color === 'blue' ? 'bg-blue-500 text-white' : pkg.color === 'green' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
                {pkg.badge}
              </span>
            </div>
            {formValues[`show_${pkg.key}`] && (
              <div className="flex items-center gap-1 mt-2 ml-6">
                <span className={`text-sm ${pkg.color === 'black' ? 'text-gray-400' : ''}`}>£</span>
                <Input
                  type="number"
                  {...register(`${pkg.key === 'loading_crew' ? 'loading_crew' : pkg.key === 'man_and_van' ? 'man_and_van' : pkg.key === 'comfort_plus' ? 'comfort_plus' : pkg.key === 'economy' ? 'economy_move' : pkg.key === 'comfort' ? 'comfort_move' : 'premium'}_price`, { valueAsNumber: true })}
                  className={`h-8 w-20 ${pkg.color === 'black' ? 'bg-gray-800 border-gray-600 text-white' : ''}`}
                />
                {(pkg.key === 'loading_crew' || pkg.key === 'man_and_van') && <span className="text-xs text-gray-500">/hr</span>}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>

    <Card className="border-2 border-[#D4AF37]/40">
      <div className="bg-[#D4AF37] px-3 py-2">
        <h3 className="font-bold text-black flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4" />
          Deposit
        </h3>
      </div>
      <CardContent className="p-3 bg-black">
        <div className="flex items-center gap-2">
          <span className="text-[#D4AF37] font-bold">£</span>
          <Input type="number" {...register("deposit_amount", { valueAsNumber: true })} className="h-9 w-24 bg-gray-800 border-gray-600 text-[#D4AF37] font-bold" />
        </div>
      </CardContent>
    </Card>

    <Card className="border-2 border-[#D4AF37]/40">
      <div className="bg-yellow-50 px-3 py-2 border-b border-[#D4AF37]">
        <h3 className="font-bold text-amber-700 flex items-center gap-2 text-sm">
          <StickyNote className="h-4 w-4" />
          Additional Notes
        </h3>
      </div>
      <CardContent className="p-3">
        <Textarea {...register("additional_notes")} placeholder="Notes for customer..." className="min-h-[80px]" />
      </CardContent>
    </Card>

    {/* CREW UPGRADE RECOMMENDATION - Quote Tab */}
    <Card className="border-2 border-amber-400 bg-amber-50" data-testid="crew-recommendation-quote">
      <div className="bg-amber-500 px-3 py-2 flex items-center justify-between">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <Users className="h-4 w-4" />
          Crew Upgrade Recommendation
        </h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-white font-medium">Show in Email</span>
          <input 
            type="checkbox" 
            {...register("show_crew_recommendation")}
            className="h-4 w-4 rounded border-white"
          />
        </label>
      </div>
      {formValues.show_crew_recommendation && (
        <CardContent className="p-3 space-y-3">
          <div className="bg-white rounded-lg p-3 border border-amber-300">
            <p className="text-sm text-gray-700 mb-3 font-medium">
              This Quote Includes:
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <Label className="text-xs font-semibold text-amber-800">Crew Size</Label>
                <Input 
                  {...register("booked_crew_count")} 
                  placeholder="2"
                  className="h-10 mt-1 border-amber-300 font-bold text-center"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-amber-800">Van Type</Label>
                <Input 
                  {...register("booked_van_type")} 
                  placeholder="3.5T Van"
                  className="h-10 mt-1 border-amber-300 font-bold text-center"
                />
              </div>
            </div>
          </div>
          
          <div className="bg-green-100 rounded-lg p-3 border-2 border-green-400">
            <p className="text-sm text-green-800 font-semibold mb-2">
              💡 For a faster & smoother move we recommend:
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold text-green-700">Recommended Crew</Label>
                <Input 
                  {...register("recommended_crew_count")} 
                  placeholder="3"
                  className="h-10 mt-1 border-green-400 font-bold text-center bg-white"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-green-700">Recommended Van</Label>
                <Input 
                  {...register("recommended_van_type")} 
                  placeholder="7.5T Truck"
                  className="h-10 mt-1 border-green-400 font-bold text-center bg-white"
                />
              </div>
            </div>
            
            <div className="mt-3 bg-green-200 rounded-lg p-2 text-center">
              <Label className="text-xs font-semibold text-green-800">Extra Cost for Upgrade</Label>
              <div className="flex items-center justify-center gap-1 mt-1">
                <span className="text-lg font-bold text-green-800">+£</span>
                <Input 
                  {...register("crew_upgrade_cost")} 
                  placeholder="150"
                  className="h-10 w-20 border-green-500 font-bold text-xl text-center bg-white"
                />
              </div>
            </div>
            
            <p className="text-xs text-green-700 mt-2 text-center">
              ⏱️ Save time & reduce stress with extra help!
            </p>
          </div>
        </CardContent>
      )}
    </Card>

    {/* WORKING HOURS & OVERTIME POLICY - Quote Tab */}
    <Card className="border-2 border-gray-700 bg-gray-50" data-testid="overtime-policy-card">
      <div className="bg-gray-900 px-3 py-2 flex items-center justify-between">
        <h3 className="font-bold text-amber-400 flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4" />
          Working Hours & Overtime Policy
        </h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-white font-medium">Show in Email</span>
          <input
            type="checkbox"
            {...register("show_overtime_policy")}
            data-testid="show-overtime-policy-checkbox"
            className="h-4 w-4 rounded border-white"
          />
        </label>
      </div>
      {formValues.show_overtime_policy && (
        <CardContent className="p-3 space-y-3">
          <p className="text-xs text-gray-600">
            Adds a policy block to the quote: crew works standard hours, anything after is charged as overtime — and nudges the customer to book the recommended crew to avoid it.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Start Time</Label>
              <Input
                {...register("work_hours_start")}
                placeholder="9:00 AM"
                data-testid="work-hours-start-input"
                className="h-10 mt-1 font-bold text-center"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">End Time</Label>
              <Input
                {...register("work_hours_end")}
                placeholder="5:00 PM"
                data-testid="work-hours-end-input"
                className="h-10 mt-1 font-bold text-center"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-red-700">Overtime £/hour</Label>
              <Input
                {...register("overtime_rate")}
                placeholder="120"
                data-testid="overtime-rate-input"
                className="h-10 mt-1 border-red-300 font-bold text-center"
              />
            </div>
          </div>
          <p className="text-[11px] text-gray-500">
            💡 Tip: turn on the Crew Upgrade Recommendation above too — the policy tells customers that booking the recommended crew avoids overtime charges.
          </p>
        </CardContent>
      )}
    </Card>

    {/* CUSTOM MESSAGE TO CUSTOMER - Quote Tab */}
    <Card className="border-2 border-purple-400 bg-purple-50" data-testid="custom-recommendation-quote">
      <div className="bg-purple-600 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <MessageCircle className="h-4 w-4" />
          Your Message to Customer
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <p className="text-xs text-purple-700">Write a personal recommendation or message:</p>
        
        <Textarea 
          {...register("custom_recommendation_message")} 
          placeholder="Example: Based on the items you mentioned, I recommend our Comfort Move package for the best experience..."
          className="min-h-[100px] text-sm border-2 border-purple-300 focus:border-purple-500"
          style={{ fontSize: '15px' }}
        />
        
        <div>
          <Label className="text-xs font-semibold text-purple-700 mb-2 block">Quick Templates:</Label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `Based on your property size and the items you mentioned, I recommend our Comfort Move package. It includes furniture dismantling & reassembly, careful loading/unloading, and placing boxes in the correct rooms. This gives you the best value for a stress-free move!`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              📦 Package Recommendation
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `I noticed you have some large furniture items. Our team is fully equipped to handle wardrobes, beds, and heavy sofas. We'll wrap everything in protective blankets and ensure safe transport. If you have any particularly fragile or valuable items, please let us know so we can give them extra attention!`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              🛋️ Large Furniture
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `We understand moving can be stressful, especially if you'd like to take it easy or have mobility concerns. For your peace of mind, we offer a full packing service where our professional team will carefully pack ALL your belongings - from fragile china to heavy books. You just relax and let us handle everything! This is perfect for elderly customers, busy professionals, or anyone who wants a completely stress-free move.`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              👴 Packing for Elderly/Relaxed Move
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `Great news! We have availability on your requested date. To secure your booking, we require a deposit of £${formValues.deposit_amount || '100'}. This guarantees your slot and allows us to allocate the right team and vehicle for your move. The remaining balance is due on the moving day.`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              ✅ Availability Confirmed
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input 
            type="checkbox" 
            {...register("show_custom_message")}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label className="text-xs">Show this message in email</Label>
        </div>
      </CardContent>
    </Card>
  </>
);
